import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Button,
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    SelectChangeEvent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { User, SiteUser } from "../../types";
import { useSelector } from "react-redux";
import { selectSiteRoles, selectRoles } from "../../userManagementSlice";
import theme from "../../../../app/theme";

interface EditRolesModalProps {
    open: boolean;
    user: User | SiteUser | null;
    onClose: () => void;
    onSave: (userId: string, newRoles: string[]) => void;
}

const EditRolesModal: React.FC<EditRolesModalProps> = ({ open, user, onClose, onSave }) => {
    const [selectedRole, setSelectedRole] = useState<string>("");
    const [roles, setRoles] = useState<string[]>([]);
    const availableRoles = useSelector(selectRoles) ?? [];
    const siteRoles = useSelector(selectSiteRoles);

    useEffect(() => {
        if (open && user) {
            const currentRoles = user.roles || [];
            setRoles(currentRoles);
            setSelectedRole("");
        }
    }, [open, user]);

    if (!user) return null;

    const handleRoleChange = (event: SelectChangeEvent<string>) => {
        setSelectedRole(event.target.value);
    };

    const handleAddRole = () => {
        if (!selectedRole) return;

        const isAlreadyInRoles = roles.some((r) => r === selectedRole);

        if (!isAlreadyInRoles) {
            setRoles([...roles, selectedRole]);
        }
        setSelectedRole("");
    };

    const handleRemoveRole = (role: string) => {
        setRoles(roles.filter((r) => r !== role));
    };

    const handleSave = () => {
        onSave(user.uuid, roles);
        onClose();
    };

    const isSiteUser = (u: User | SiteUser): u is SiteUser => "sites" in u;
    const availableRolesFiltered = (isSiteUser(user) ? siteRoles : availableRoles)?.filter(
        (role) => !roles.some((r) => r === role)
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ bgcolor: theme.palette.background.paper, color: "#fff", pb: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h6">Edit User Roles</Typography>
                        <Typography variant="body2" color="textSecondary">
                            Manage role assignments for {user.email}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} sx={{ color: "#fff" }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ bgcolor: theme.palette.background.paper, color: "#fff", pt: 20, pb: 3 }}>
                <Box mb={20}>
                    <Typography variant="subtitle2" sx={{ mb: 8, color: "#fff" }}>
                        Role
                    </Typography>
                    <Box display="flex" gap={8}>
                        <FormControl fullWidth size="small">
                            <InputLabel sx={{ color: "#aaa" }}>
                                {availableRolesFiltered && availableRolesFiltered.length === 0
                                    ? "All roles assigned"
                                    : "Search roles..."}
                            </InputLabel>
                            <Select
                                value={selectedRole}
                                onChange={handleRoleChange}
                                label={
                                    availableRolesFiltered && availableRolesFiltered.length === 0
                                        ? "All roles assigned"
                                        : "Search roles..."
                                }
                                disabled={!availableRolesFiltered || availableRolesFiltered.length === 0}
                                sx={{
                                    "bgcolor": theme.palette.background.paper,
                                    "color": "#fff",
                                    ".MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
                                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#666" },
                                    ".MuiSvgIcon-root": { color: "#aaa" },
                                }}
                            >
                                {availableRolesFiltered?.map((role) => (
                                    <MenuItem key={role} value={role}>
                                        {role}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button
                            variant="contained"
                            sx={{
                                minWidth: "80px",
                            }}
                            onClick={handleAddRole}
                            disabled={!selectedRole || !availableRolesFiltered || availableRolesFiltered.length === 0}
                        >
                            Add
                        </Button>
                    </Box>
                </Box>

                <Box>
                    <Typography variant="subtitle2" sx={{ mb: 8, color: "#aaa" }}>
                        Assigned Roles
                    </Typography>
                    <Box sx={{ bgcolor: theme.palette.background.paper, borderRadius: 1, p: 10, minHeight: "60px" }}>
                        {roles.length > 0 ? (
                            <Box display="flex" flexWrap="wrap" gap={8}>
                                {roles.map((role) => (
                                    <Chip
                                        key={role}
                                        label={role}
                                        onDelete={() => handleRemoveRole(role)}
                                        sx={{
                                            "bgcolor": "rgba(76, 175, 80, 0.15)",
                                            "color": "#4caf50",
                                            "border": "1px solid #4caf50",
                                            ".MuiChip-deleteIcon": { color: "#4caf50" },
                                        }}
                                    />
                                ))}
                            </Box>
                        ) : (
                            <Typography variant="body2" sx={{ color: "#666" }}>
                                No roles assigned
                            </Typography>
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ bgcolor: theme.palette.background.paper, py: 16, px: 24 }}>
                <Button onClick={onClose} sx={{ mr: 2 }}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={roles.length === 0}>
                    Save Roles ({roles.length})
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditRolesModal;
